/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useUser } from '#hooks/UNSAFE_useUser';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { classNames } from '#utils/UNSAFE_classNames';
import { CircleWrapper, CircleInner } from '#PRIVATE_Meter';
import { CircularReferenceLine } from './CircularReferenceLine';
import { TrackResizeContainer } from '#UNSAFE_TrackResizeContainer';

import {
  validateRange,
  getTrackAndIndicatorColor,
  getMeterAriaProps,
  getCircleSection,
  getPositiveAngle
} from '#utils/PRIVATE_meterUtils';

import { getDimensions, getValue, getCenterContext } from './circleMeterUtils';

import {
  getIndicatorProps,
  getTrackProps,
  getThresholdsProps,
  MeterInfo,
  getWidthAndHeight,
  getFitMaxWidth,
  getBreakPoint,
  getCenterOffsets,
  getFitHalfCircleOffset
} from './layoutUtils';

import {
  usePointerEvents,
  useKeyboardEvents,
  useMeterDatatip
} from '#hooks/PRIVATE_useVisMeterEvents';

import { MeterProps, CircularRefLine } from './meterCircle.types';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { MeterCircleStyles, MeterCircleVariantOptions } from './themes/MeterCircleStyles.css';
import { MeterCircleRedwoodTheme } from './themes/redwood/MeterCircleTheme';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import {
  getReferenceLineLabelGap,
  getReferenceLinesProps,
  getWidthAndHeightWithReferenceLine
} from './refLineUtils';
import { useCssVars } from '#hooks/PRIVATE_useCssVars';
import {
  getDefaultReferenceLineLabelFontSize,
  getMaxRefLabelDims,
  hasLabelledReferenceLine
} from '#utils/PRIVATE_meterUtils/meterUtils';
import { useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { typographyVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';

const FIT_INNERRADIUS = 0.8;
const FIT_BUFFER = 8;

/**
 * A Meter circle displays information graphically in a circular bar, highlighting a specific metric value's progress in relation to its min, max, or thresholds.
 */
export function FitMeterCircle({
  max = 100,
  min = 0,
  value = 0,
  step = 1,
  size = 'fit',
  startAngle = 90,
  angleExtent = 360,
  isTrackRendered = true,
  thresholdDisplay = 'all',
  indicatorSize = 1,
  testId,
  isReadonly,
  ...props
}: MeterProps) {
  const testIdProps = useTestId(testId);
  validateRange(min, max, value, step, false, isReadonly);
  const isInteractive = props.onCommit || props.onInput;
  const { classes, styles } = useComponentTheme<MeterCircleVariantOptions, MeterCircleStyles>(
    MeterCircleRedwoodTheme,
    {
      size,
      interaction: isInteractive ? 'isInteractive' : undefined
    }
  );

  return (
    <TrackResizeContainer
      class={classNames([classes, styles.textAlignCenter])}
      width={'100%'}
      height={'100%'}>
      {function _(_width, _height) {
        _width -= FIT_BUFFER;
        _height -= FIT_BUFFER;

        const hasReferenceLabel = hasLabelledReferenceLine(props.referenceLines);

        const { textMeasureContent, getTextDimensions } = useTextDimensions();
        const { direction } = useUser();
        const isRtl = direction === 'rtl';

        let sAngle = startAngle;
        if (isRtl) {
          sAngle = getPositiveAngle(180 - sAngle);
        }

        const section = getCircleSection(sAngle, angleExtent, isRtl);

        const { width: _w, height: _h } = getWidthAndHeight(_width, _height, section);
        const breakPoint = getBreakPoint(Math.max(_w, _h));

        const fontSize = getDefaultReferenceLineLabelFontSize(breakPoint);

        const circleVars = useMemo(
          () => [
            {
              tokenVar: fontSize,
              key: 'labelFontSize',
              cssProp: 'font-size'
            },
            {
              tokenVar: typographyVars.body.md.lineHeight,
              key: 'lineHeight',
              cssProp: 'line-height'
            }
          ],
          [fontSize]
        );

        const { resolvedVars, cssContent } = useCssVars(circleVars);

        const { maxRefLabelDims } = getMaxRefLabelDims(
          resolvedVars['fontSize'],
          resolvedVars['lineHeight'],
          props.referenceLines,
          getTextDimensions
        );

        const isMin = breakPoint === 'xs' || breakPoint === 'min';
        const innerR = props.innerRadius ?? FIT_INNERRADIUS;

        const {
          width: w,
          height: h,
          overHang
        } = getWidthAndHeightWithReferenceLine(
          _width,
          _height,
          !!props.referenceLines?.length,
          breakPoint,
          hasReferenceLabel,
          maxRefLabelDims
        );

        const { width, height } = getWidthAndHeight(w, h, section);

        const radius = section === 'full' ? Math.min(width, height) / 2 : Math.min(width, height);
        const diameter = radius * 2;

        const currentInputRef = useRef<number>();

        const dimensionsRef = useRef(getDimensions(null, section));
        const circleWrapperRef = useRef<HTMLDivElement>(null);
        const [isDimsReady, setDimsReady] = useState<boolean>(false);

        useEffect(() => {
          if (props.onCommit || props.onInput || props.children) {
            dimensionsRef.current = getDimensions(circleWrapperRef.current, section);
            setDimsReady(true);
          }
        }, [section]);

        const getValueFromEvent = (event: PointerEvent) => {
          const circleContainer = circleWrapperRef.current;
          if (event.target == circleContainer) {
            return getValue(
              event,
              min,
              max,
              step,
              dimensionsRef,
              sAngle,
              angleExtent,
              section,
              isRtl
            );
          }
          return;
        };

        const isPointerActiveRef = useRef(false);

        const pointerEventsProps = usePointerEvents(
          value,
          getValueFromEvent,
          circleWrapperRef,
          currentInputRef,
          isPointerActiveRef,
          props.onCommit,
          props.onInput
        );

        const keyboardEventsProps = useKeyboardEvents(
          value,
          min,
          max,
          step,
          currentInputRef,
          props.onCommit,
          props.onInput
        );

        const { datatipContent, datatipProps } = useMeterDatatip(
          value,
          isPointerActiveRef,
          props.datatip,
          props['aria-describedby']
        );

        const ariaProps = getMeterAriaProps(
          value,
          min,
          max,
          props['aria-label'],
          props['aria-labelledby'],
          props.thresholds,
          isReadonly
        );
        const mergedProps = mergeProps(
          pointerEventsProps,
          keyboardEventsProps,
          datatipProps,
          ariaProps
        );

        const { trackColor, indicatorColor } = getTrackAndIndicatorColor(
          value,
          thresholdDisplay,
          props.trackColor,
          props.indicatorColor,
          props.thresholds
        );

        const meterInfo = {
          min,
          max,
          value,
          startAngle: sAngle,
          angleExtent,
          size,
          section,
          isRtl,
          innerRadius: dimensionsRef.current.innerRadius
        } as MeterInfo;

        const { isTabbable } = useTabbableMode();
        const centerContext = getCenterContext(dimensionsRef, section);
        return (
          <>
            <div
              class={classNames([
                classes,
                section === 'full' ? styles.meterCircleFullSizeStyle : '',
                styles.textAlignInitial
              ])}
              style={{
                width: _width,
                height: _height
              }}
              tabIndex={!isTabbable ? -1 : 0}
              {...testIdProps}
              {...mergedProps}>
              <CircleWrapper
                wrapperRef={circleWrapperRef}
                class={classNames([
                  styles.circleWrapperBaseStyle,
                  isInteractive ? styles.interactiveStyle : '',
                  props.referenceLines && props.referenceLines.length > 0
                    ? size && styles[`${section}Fit${isMin ? 'Sm' : 'Lg'}Style`]
                    : '',
                  isRtl ? styles.centerDivRtl : styles.centerDivLtr
                ])}
                style={{
                  width: `${width}px`,
                  height: `${height}px`,
                  // correction for margin
                  transform: `translate(${getCenterOffsets(section, overHang, isRtl)})`
                }}>
                <CircleInner
                  width={`${width}px`}
                  height={`${height}px`}
                  class={classNames([styles.circleTrackBaseStyle, styles[`${section}Style`]])}
                  {...getTrackProps(
                    meterInfo,
                    isTrackRendered,
                    thresholdDisplay,
                    trackColor,
                    innerR,
                    props.thresholds,
                    diameter
                  )}
                />
                {thresholdDisplay === 'all' &&
                  props.thresholds &&
                  getThresholdsProps(
                    meterInfo,
                    props.thresholds,
                    isTrackRendered,
                    trackColor,
                    innerR,
                    diameter
                  ).map((threshold) => {
                    return (
                      <CircleInner
                        class={classNames([styles.circleTrackBaseStyle, styles[`${section}Style`]])}
                        {...threshold}
                        width={`${width}px`}
                        height={`${height}px`}
                      />
                    );
                  })}
                <CircleInner
                  color={indicatorColor}
                  class={classNames([
                    styles.circleValueBaseStyle,
                    styles[`circleValue${section}Style`],
                    styles[`${section}Style`]
                  ])}
                  {...getIndicatorProps(meterInfo, indicatorSize, innerR, 2 * radius)}
                />
                {props.referenceLines?.map((refLine: CircularRefLine) => {
                  const { radius, length, angle, color } = getReferenceLinesProps(
                    meterInfo,
                    refLine,
                    innerR,
                    isMin,
                    diameter
                  );
                  const labelMaxWidth = getFitMaxWidth(
                    section,
                    _width,
                    angle,
                    diameter / 2 + overHang + getReferenceLineLabelGap(breakPoint, angle)
                  );
                  return (
                    <CircularReferenceLine
                      label={refLine.label}
                      labelStyle={{
                        fontSize: resolvedVars['labelFontSize'],
                        lineHeight: typographyVars.body.md.lineHeight,
                        ...refLine.labelStyle
                      }}
                      labelMaxWidth={labelMaxWidth}
                      radius={radius}
                      length={length}
                      angle={angle}
                      labelOffset={getReferenceLineLabelGap(breakPoint, angle)}
                      color={color}
                      section={section}
                      halfMeterOffset={getFitHalfCircleOffset(_width, diameter / 2, section, isRtl)}
                    />
                  );
                })}
                {isDimsReady && props.children && centerContext && (
                  <div class={classNames([styles.meterCircleCenterContent])}>
                    {props.children(centerContext)}
                  </div>
                )}
              </CircleWrapper>
            </div>
            {datatipContent}
            {textMeasureContent}
            {cssContent}
          </>
        );
      }}
    </TrackResizeContainer>
  );
}
